import * as React from "react";
import { Modal, Form, Select, Switch, Spin } from "antd";
import { snackbarStore } from "src/store/snackbarStore";
import CustomButton from "src/components/common/CustomButton";
import AppInput from "src/components/common/AppInput";
import type { WarehouseListItem } from "src/api/warehouse/warehouseApi";
import type { WarehouseUpsertBody } from "src/types/warehouse";
import {
  getWarehouseApi,
  createWarehouseApi,
  updateWarehouseApi,
} from "src/api/warehouse/warehouseApi";
import { getDelegateListApi } from "src/api/delegate/delegateApi";
import type { DelegateListItem } from "src/api/delegate/delegateApi";

type Props = {
  open: boolean;
  editingItem: WarehouseListItem | null;
  onClose: () => void;
  onSuccess: () => void;
};

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

export default function WarehouseUpsertModal({ open, editingItem, onClose, onSuccess }: Props) {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [delegateList, setDelegateList] = React.useState<DelegateListItem[]>([]);

  const isEdit = !!editingItem;

  // Load delegate list on mount
  React.useEffect(() => {
    getDelegateListApi({ page: 0, size: 1000, order: "delegateName,asc" })
      .then((res) => {
        if (res.statusCode === 1 && res.data) setDelegateList(res.data.content);
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    if (isEdit) {
      const fetchDetail = async () => {
        try {
          setLoading(true);
          const res = await getWarehouseApi(editingItem.warehouseId);
          if (res.statusCode !== 1 || !res.data) {
            toast(res.message || "창고 정보를 불러올 수 없습니다.", "error");
            onClose();
            return;
          }

          const data = res.data;
          form.setFieldsValue({
            warehouseCode: data.warehouseCode,
            warehouseName: data.warehouseName,
            inventoryType: data.inventoryType,
            externalProvider: data.externalProvider,
            externalWarehouseId: data.externalWarehouseId,
            delegateId: data.delegateId,
            isActive: data.isActive,
          });
        } catch (e: any) {
          toast(e?.message || "창고 정보를 불러오는 중 오류가 발생했습니다.", "error");
          onClose();
        } finally {
          setLoading(false);
        }
      };
      fetchDetail();
    } else {
      form.setFieldsValue({
        inventoryType: "MANUAL",
        isActive: true,
      });
    }
  }, [open, isEdit, editingItem, form, onClose]);

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);

      const payload: WarehouseUpsertBody = {
        warehouseCode: values.warehouseCode,
        warehouseName: values.warehouseName,
        inventoryType: values.inventoryType,
        externalProvider: values.externalProvider || undefined,
        externalWarehouseId: values.externalWarehouseId || undefined,
        delegateId: values.delegateId || undefined,
        isActive: values.isActive ?? true,
      };

      let res;
      if (isEdit) {
        res = await updateWarehouseApi(editingItem.warehouseId, payload);
      } else {
        res = await createWarehouseApi(payload);
      }

      if (res.statusCode === 1) {
        toast(isEdit ? "창고가 수정되었습니다." : "창고가 등록되었습니다.", "success");
        onSuccess();
      } else {
        toast(res?.message || `창고 ${isEdit ? "수정" : "등록"} 실패`, "error");
      }
    } catch (e: any) {
      toast(e?.message || "요청 중 오류가 발생했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "창고 수정" : "창고 등록"}
      open={open}
      onCancel={onClose}
      maskClosable={false}
      width={600}
      footer={[
        <CustomButton key="cancel" variant="outlined" onClick={onClose} disabled={submitting}>
          취소
        </CustomButton>,
        <CustomButton
          key="submit"
          variant="contained"
          onClick={() => form.submit()}
          disabled={submitting || loading}
        >
          {submitting ? (isEdit ? "수정 중..." : "등록 중...") : isEdit ? "수정" : "등록"}
        </CustomButton>,
      ]}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin />
        </div>
      ) : (
        <>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item label="활성 상태" name="isActive" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              label="창고 코드"
              name="warehouseCode"
              rules={[{ required: true, message: "창고 코드를 입력하세요" }]}
            >
              <AppInput placeholder="창고 코드 입력 (예: WH-001)" disabled={isEdit} />
            </Form.Item>

            <Form.Item
              label="창고명"
              name="warehouseName"
              rules={[{ required: true, message: "창고명을 입력하세요" }]}
            >
              <AppInput placeholder="창고명 입력" />
            </Form.Item>

            <Form.Item
              label="재고관리 타입"
              name="inventoryType"
              rules={[{ required: true, message: "재고관리 타입을 선택하세요" }]}
            >
              <Select
                options={[
                  { label: "수동(MANUAL)", value: "MANUAL" },
                  { label: "WMS", value: "WMS" },
                  { label: "입점사", value: "DELEGATE" },
                ]}
              />
            </Form.Item>

            <Form.Item label="외부 공급자" name="externalProvider">
              <AppInput placeholder="외부 공급자 (예: FASSTO)" />
            </Form.Item>

            <Form.Item label="외부 창고 ID" name="externalWarehouseId">
              <AppInput placeholder="외부 창고 ID" />
            </Form.Item>

            <Form.Item label="위탁사" name="delegateId">
              <Select placeholder="위탁사 선택" allowClear showSearch optionFilterProp="label">
                <Select.Option value={null} label="없음">
                  없음
                </Select.Option>
                {delegateList.map((d) => (
                  <Select.Option key={d.id} value={d.id} label={d.delegateName}>
                    {d.delegateName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}
